/**
 * API 쿼리 훅
 * 
 * React Query를 래핑한 커스텀 훅입니다.
 * 인증 토큰 자동 추가, 에러 처리 등을 통합합니다.
 * 
 * 사용법:
 * const { data, isLoading, error } = useApiQuery(
 *   ['reservations', artistId],
 *   '/api/reservations',
 *   { artist_id: artistId }
 * );
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { buildAuthHeaders, handleAuthError } from '../utils/auth';

/**
 * 기본 fetch 함수
 * @param {string} endpoint - API 엔드포인트
 * @param {Object} params - 쿼리 파라미터
 * @returns {Promise<any>} 응답 데이터
 */
const apiFetch = async (endpoint, params = {}) => {
    // 쿼리 스트링 생성
    const queryString = Object.keys(params).length > 0
        ? '?' + new URLSearchParams(params).toString()
        : '';

    const response = await fetch(`${endpoint}${queryString}`, {
        headers: buildAuthHeaders({
            'Content-Type': 'application/json',
        }),
        credentials: 'include',
    });

    if (response.status === 401) {
        handleAuthError(response);
        throw new Error('Unauthorized');
    }

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `HTTP ${response.status}`);
        error.status = response.status;
        throw error;
    }

    return response.json();
};

/**
 * API Query 훅
 * 
 * @param {Array|string} queryKey - React Query 캐시 키
 * @param {string} endpoint - API 엔드포인트
 * @param {Object} params - 쿼리 파라미터
 * @param {Object} options - React Query 옵션
 * @returns {Object} useQuery 반환값
 */
export const useApiQuery = (queryKey, endpoint, params = {}, options = {}) => {
    return useQuery({
        queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
        queryFn: () => apiFetch(endpoint, params),
        ...options,
    });
};

/**
 * API Mutation 훅 (POST/PUT/DELETE)
 * 
 * @param {string} endpoint - API 엔드포인트
 * @param {Object} options - 옵션
 * @param {string} options.method - HTTP 메서드 (기본: 'POST')
 * @param {Array} options.invalidateQueries - 성공 시 무효화할 쿼리 키들
 * @param {Function} options.onSuccess - 성공 콜백
 * @param {Function} options.onError - 에러 콜백
 * @returns {Object} useMutation 반환값
 */
export const useApiMutation = (endpoint, options = {}) => {
    const queryClientInstance = useQueryClient();
    const { method = 'POST', invalidateQueries = [], onSuccess, onError, ...mutationOptions } = options;

    return useMutation({
        mutationFn: async (data) => {
            const response = await fetch(endpoint, {
                method,
                headers: buildAuthHeaders({
                    'Content-Type': 'application/json',
                }),
                body: data ? JSON.stringify(data) : undefined,
                credentials: 'include',
            });

            if (response.status === 401) {
                handleAuthError(response);
                throw new Error('Unauthorized');
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const error = new Error(errorData.message || `HTTP ${response.status}`);
                error.status = response.status;
                throw error;
            }

            // 204 No Content
            if (response.status === 204) {
                return null;
            }

            return response.json();
        },
        onSuccess: (data, variables, context) => {
            // 지정된 쿼리들 무효화
            invalidateQueries.forEach((key) => {
                queryClientInstance.invalidateQueries({ queryKey: key });
            });

            if (onSuccess) {
                onSuccess(data, variables, context);
            }
        },
        onError: (error, variables, context) => {
            console.error('[useApiMutation] Error:', error);
            if (onError) {
                onError(error, variables, context);
            }
        },
        ...mutationOptions,
    });
};

/**
 * 예약 목록 조회 훅
 * 
 * @param {number|string} artistId - 아티스트 ID
 * @param {Object} filters - 필터 옵션
 * @returns {Object} 예약 데이터 및 상태
 */
export const useReservationsQuery = (artistId, filters = {}) => {
    return useApiQuery(
        ['reservations', 'list', artistId, filters],
        '/api/reservations',
        { artist_id: artistId, ...filters },
        {
            enabled: !!artistId,
            staleTime: 2 * 60 * 1000, // 2분
        }
    );
};

/**
 * 휴무일 조회 훅
 * 
 * @param {number|string} artistId - 아티스트 ID
 * @returns {Object} 휴무일 데이터 및 상태
 */
export const useDayOffsQuery = (artistId) => {
    return useApiQuery(
        ['dayOffs', 'artist', artistId],
        '/api/artist-dayoff',
        { artist_id: artistId },
        {
            enabled: !!artistId,
            staleTime: 5 * 60 * 1000, // 5분
        }
    );
};

/**
 * 이벤트 목록 조회 훅
 * 
 * @param {number|string} userId - 사용자 ID
 * @param {Object} dateRange - 날짜 범위 { start, end }
 * @returns {Object} 이벤트 데이터 및 상태
 */
export const useEventsQuery = (userId, dateRange = {}) => {
    return useApiQuery(
        ['events', 'list', userId, dateRange],
        '/api/events',
        { user_id: userId, ...dateRange },
        {
            enabled: !!userId,
            staleTime: 1 * 60 * 1000, // 1분
        }
    );
};

export default useApiQuery;
